import { useEffect, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { NotesWrapper } from '@/components/ats/notes';
import { useShowToast } from '@/hooks';
import { atsInterviewActions, getAtsInterviews } from '@/store/interviews';
import {
  useCreateInterviewNoteMutation,
  useUpdateInterviewNoteMutation,
  useGetInterviewNotesMutation,
} from '@/store/query/interviews';
import { useDispatch, useSelector } from 'react-redux';

interface Note {
  id: string;
  body: string;
  private: boolean;
  shared: boolean;
  tagged_users: Array<{ id: number }>;
  created_by: {
    id: number;
    name: string;
    avatar: string;
    email: string;
    uuid: string;
    is_ai_coordinator: boolean;
  };
}

interface NotePayload {
  body: string;
  private: boolean;
  shared: boolean;
  tagged_user_ids: number[];
}

interface HandleNoteAddProps {
  payload: NotePayload;
  scrollToBottom?: () => void;
}

interface HandleNoteUpdateProps {
  payload: NotePayload;
  note: Note;
  setEditMode: (_value: boolean) => void;
}

interface HandleNoteAccessChangeProps {
  payload: NotePayload;
  note: Note;
}

interface ApiResponse<T> {
  data: T;
  meta: {
    page: number;
    pages: number;
    count: number;
  };
}

export const InterviewNotes = () => {
  const { interviewId } = useParams();
  const { notes: notesData } = useSelector(getAtsInterviews);
  const { data: notes = [], meta: notesMeta } = notesData;
  const [isInitialLoaded, setInitialLoaded] = useState(false);

  const dispatch = useDispatch();
  const { addInterviewNotes, addInterviewNote, updateInterviewNote } =
    atsInterviewActions;
  const [createNote] = useCreateInterviewNoteMutation();
  const [updateNote, { isLoading: isUpdating }] =
    useUpdateInterviewNoteMutation();
  const [getInterviewNotes, { isLoading }] = useGetInterviewNotesMutation();
  const toast = useShowToast();

  const fetchNotes = async () => {
    if (!interviewId) return;

    const result = await getInterviewNotes({
      interviewId,
      params: { page: !isInitialLoaded ? 1 : notesMeta?.page + 1 },
    });

    if ('data' in result) {
      const response = result?.data as ApiResponse<Note[]>;
      dispatch(
        addInterviewNotes({
          notes: response?.data,
          meta: response?.meta,
          isInitial: !isInitialLoaded,
        })
      );
      setInitialLoaded(true);
    }
  };

  useEffect(() => {
    if (!interviewId) return;
    setInitialLoaded(false);
    fetchNotes();
  }, [interviewId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLoadMore = () => {
    fetchNotes();
  };

  const handleNoteAdd = async (props: HandleNoteAddProps) => {
    if (!interviewId) return;

    const result = await createNote({ interviewId, payload: props.payload });
    if ('error' in result) {
      toast({ title: 'Adding note failed', status: 'error' });
    } else {
      dispatch(addInterviewNote({ newNote: result.data }));
      toast({ title: 'Added note successfully' });
      props.scrollToBottom?.();
    }
  };

  const handleNoteAccessChange = async (props: HandleNoteAccessChangeProps) => {
    if (!interviewId) return;

    const result = await updateNote({
      interviewId,
      payload: props.payload,
      noteId: props.note?.id,
    });
    if ('error' in result) {
      toast({ title: 'Updating note access failed', status: 'error' });
    } else {
      dispatch(updateInterviewNote({ note: props.note, newNote: result.data }));
      toast({ title: 'Note access updated successfully' });
    }
  };

  const handleNoteUpdate = async (props: HandleNoteUpdateProps) => {
    if (!interviewId) return;

    const result = await updateNote({
      interviewId,
      payload: props.payload,
      noteId: props.note?.id,
    });
    if ('error' in result) {
      toast({ title: 'Updating note failed', status: 'error' });
    } else {
      dispatch(updateInterviewNote({ note: props.note, newNote: result.data }));
      toast({ title: 'Updated note successfully' });
      props.setEditMode(false);
    }
  };

  return (
    <Flex alignItems="center" justifyContent="center" h="100%">
      <NotesWrapper
        isLoading={!isInitialLoaded}
        notes={notes}
        handleNoteAdd={handleNoteAdd as unknown as () => void}
        handleNoteAccessChange={handleNoteAccessChange as unknown as () => void}
        isUpdating={isUpdating}
        handleNoteUpdate={handleNoteUpdate as unknown as () => void}
        meta={notesMeta}
        onLoadMore={onLoadMore}
        isMoreLoading={isLoading}
        type="interview"
      />
    </Flex>
  );
};
